import { Routes, Route, Link, Navigate, useLocation, useParams } from 'react-router-dom';
import { ASSESSMENT, CHECKLIST, WORKSPACES } from './lib/routes';
import { lazy, Suspense, useEffect, useState } from 'react';
import { PageErrorBoundary } from './components/ErrorBoundary';
import { AccountDeletedNotice } from './components/AccountDeletedNotice';
import { useIsFetching } from '@tanstack/react-query';
import { AuthProvider } from './lib/auth';
import { SiteHeader } from './components/SiteHeader';
import { Footer } from './components/Footer';
import { CookieConsent } from './components/CookieConsent';

/* Shown only when a network request is genuinely slow (5s+). Neutral
   copy, no infrastructure references. Rendered only inside authenticated
   app pages (see below) so unauthenticated visitors on the marketing
   home / sign-in screens never see it. */
function SlowLoadBanner() {
  const isFetching = useIsFetching();
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (isFetching === 0) {
      setVisible(false);
      return;
    }
    const t = setTimeout(() => setVisible(true), 5000);
    return () => clearTimeout(t);
  }, [isFetching]);
  if (!visible) return null;
  return (
    <div
      role="status"
      aria-live="polite"
      className="border-b border-rule bg-paper-deep"
    >
      <div className="max-w-4xl mx-auto px-6 py-2.5 flex items-center gap-3 flex-wrap">
        <span
          className="inline-block w-3 h-3 rounded-full"
          style={{
            background: 'var(--accent)',
            animation: 'pi-pulse 1.4s ease-in-out infinite',
          }}
          aria-hidden="true"
        />
        <span className="font-editorial text-[14px] text-ink-soft">
          Loading &mdash; this may take a moment.
        </span>
      </div>
    </div>
  );
}
/* Kept in the main bundle: home, sign-in flow, dashboard (the two
   most common post-auth entry points). Everything else is lazy so
   users who only visit the assessment don't download CYBHI code,
   users who don't sign in never download the app screens, etc. */
import { HomePage } from './pages/HomePage';
import { AccountPage } from './pages/AccountPage';
import { AuthCallbackPage } from './pages/AuthCallbackPage';
import { DashboardPage } from './pages/DashboardPage';

/* Guest checklist picker + preview — lazy. */
const SelectorPage = lazy(() => import('./pages/SelectorPage').then((m) => ({ default: m.SelectorPage })));
const PreviewPage = lazy(() => import('./pages/PreviewPage').then((m) => ({ default: m.PreviewPage })));

/* CYBHI workspace + checklist pages — lazy. */
const CreateWorkspacePage = lazy(() =>
  import('./pages/CreateWorkspacePage').then((m) => ({ default: m.CreateWorkspacePage })),
);
const WorkspaceDetailPage = lazy(() =>
  import('./pages/WorkspaceDetailPage').then((m) => ({ default: m.WorkspaceDetailPage })),
);
const CreateChecklistPage = lazy(() =>
  import('./pages/CreateChecklistPage').then((m) => ({ default: m.CreateChecklistPage })),
);
const ChecklistEditPage = lazy(() =>
  import('./pages/ChecklistEditPage').then((m) => ({ default: m.ChecklistEditPage })),
);

/* Invites + admin — lazy. */
const AcceptInvitePage = lazy(() =>
  import('./pages/AcceptInvitePage').then((m) => ({ default: m.AcceptInvitePage })),
);
const AdminApprovalsPage = lazy(() =>
  import('./pages/AdminApprovalsPage').then((m) => ({ default: m.AdminApprovalsPage })),
);
const AdminNewsletterPage = lazy(() =>
  import('./pages/AdminNewsletterPage').then((m) => ({ default: m.AdminNewsletterPage })),
);

/* The weekly newsletter's unsubscribe link, and Paid Members' articles — lazy. */
const NewsletterUnsubscribePage = lazy(() =>
  import('./pages/NewsletterUnsubscribePage').then((m) => ({ default: m.NewsletterUnsubscribePage })),
);
const ArticlePage = lazy(() => import('./pages/ArticlePage').then((m) => ({ default: m.ArticlePage })));

/* Psych Safety pages — lazy (was already). */
const PsychHomePage    = lazy(() => import('./pages/psych/PsychHomePage')   .then((m) => ({ default: m.PsychHomePage })));
const PsychTakePage    = lazy(() => import('./pages/psych/PsychTakePage')   .then((m) => ({ default: m.PsychTakePage })));
const PsychResultsPage = lazy(() => import('./pages/psych/PsychResultsPage').then((m) => ({ default: m.PsychResultsPage })));
const PsychHistoryPage = lazy(() => import('./pages/psych/PsychHistoryPage').then((m) => ({ default: m.PsychHistoryPage })));

/* Redirect an old address to its new home, carrying :params and the query string. */
function Moved({ to }: { to: string }) {
  const params = useParams();
  const { search } = useLocation();
  const path = to.replace(/:(\w+)/g, (_, k: string) => encodeURIComponent(params[k] ?? ''));
  return <Navigate to={path + search} replace />;
}

/* App home: the latest from the Resource hub and the tools (AppHomePage). Right after signing out, Entra
   lands here; send the person to /account, which offers signing back in. */
const AppHomePage = lazy(() => import('./pages/AppHomePage').then((m) => ({ default: m.AppHomePage })));
function AppHome() {
  let signedOut = false;
  try {
    signedOut = !!sessionStorage.getItem('signed-out');
    sessionStorage.removeItem('signed-out');
  } catch {
    /* ignore */
  }
  return signedOut ? <Navigate to="/account?signedout=1" replace /> : <AppHomePage />;
}

function ToAccount() {
  const { search } = useLocation();
  return <Navigate to={`/account${search}`} replace />;
}

function PageFallback() {
  return (
    <div className="max-w-md mx-auto px-6 py-24 text-center font-editorial italic text-ink-muted">
      Loading…
    </div>
  );
}

export function App() {
  const { pathname } = useLocation();
  return (
    <AuthProvider>
      <div className="min-h-screen flex flex-col bg-paper">
        <SiteHeader />
        <AccountDeletedNotice />
        <SlowLoadBanner />
        <main className="flex-1">
          {/* A page that fails to draw shows a reload message instead of a blank screen; each address starts afresh. */}
          <PageErrorBoundary key={pathname}>
          <Suspense fallback={<PageFallback />}>
            <Routes>
              {/* App home: intentionally blank for now (content to come). */}
              <Route path="/" element={<AppHome />} />

              {/* CYBHI Onboarding Checklist — everything under /cybhi-checklist.
                  The picker and preview are open to everyone (kept for the tab). */}
              <Route path={CHECKLIST} element={<HomePage />} />
              <Route path={`${CHECKLIST}/select`} element={<SelectorPage />} />
              <Route path={`${CHECKLIST}/preview`} element={<PreviewPage />} />
              <Route path={`${CHECKLIST}/team/:id/checklist/new`} element={<CreateChecklistPage />} />
              <Route path={`${CHECKLIST}/team/:id/checklist/:cid`} element={<ChecklistEditPage />} />

              {/* Psychological Safety Assessment — everything under its own path. */}
              <Route path={ASSESSMENT} element={<PsychHomePage />} />
              <Route path={`${ASSESSMENT}/take`} element={<PsychTakePage />} />
              <Route path={`${ASSESSMENT}/results`} element={<PsychResultsPage />} />
              <Route path={`${ASSESSMENT}/results/:id`} element={<PsychResultsPage />} />
              <Route path={`${ASSESSMENT}/history`} element={<PsychHistoryPage />} />

              {/* Account level: sign-in, workspaces (shared by the apps), invites, admin. */}
              <Route path="/account" element={<AccountPage />} />
              <Route path={WORKSPACES} element={<DashboardPage />} />
              <Route path={`${WORKSPACES}/new`} element={<CreateWorkspacePage />} />
              <Route path={`${WORKSPACES}/:id`} element={<WorkspaceDetailPage />} />
              <Route path="/account/invite/:token" element={<AcceptInvitePage />} />
              <Route path="/account/admin/approvals" element={<AdminApprovalsPage />} />
              <Route path="/account/admin/newsletter" element={<AdminNewsletterPage />} />
              <Route path="/newsletter/unsubscribe" element={<NewsletterUnsubscribePage />} />
              <Route path="/articles/:slug" element={<ArticlePage />} />
              <Route path="/auth/callback" element={<AuthCallbackPage />} />

              {/* Old addresses → new ones (bookmarks, invite emails already sent). */}
              <Route path="/signin" element={<ToAccount />} />
              <Route path="/signup" element={<ToAccount />} />
              <Route path="/select" element={<Moved to={`${CHECKLIST}/select`} />} />
              <Route path="/preview" element={<Moved to={`${CHECKLIST}/preview`} />} />
              <Route path="/dashboard" element={<Moved to={WORKSPACES} />} />
              <Route path="/account/workspaces" element={<Moved to={WORKSPACES} />} />
              <Route path="/account/workspaces/new" element={<Moved to={`${WORKSPACES}/new`} />} />
              <Route path="/account/workspaces/:id" element={<Moved to={`${WORKSPACES}/:id`} />} />
              <Route path={`${CHECKLIST}/workspace/:id/checklist/new`} element={<Moved to={`${CHECKLIST}/team/:id/checklist/new`} />} />
              <Route path={`${CHECKLIST}/workspace/:id/checklist/:cid`} element={<Moved to={`${CHECKLIST}/team/:id/checklist/:cid`} />} />
              <Route path="/workspace/new" element={<Moved to={`${WORKSPACES}/new`} />} />
              <Route path="/workspace/:id" element={<Moved to={`${WORKSPACES}/:id`} />} />
              <Route path="/workspace/:id/checklist/new" element={<Moved to={`${CHECKLIST}/team/:id/checklist/new`} />} />
              <Route path="/workspace/:id/checklist/:cid" element={<Moved to={`${CHECKLIST}/team/:id/checklist/:cid`} />} />
              <Route path="/invite/:token" element={<Moved to="/account/invite/:token" />} />
              <Route path="/admin/approvals" element={<Moved to="/account/admin/approvals" />} />
              <Route path="/assessment" element={<Moved to={ASSESSMENT} />} />
              <Route path="/assessment/take" element={<Moved to={`${ASSESSMENT}/take`} />} />
              <Route path="/assessment/results" element={<Moved to={`${ASSESSMENT}/results`} />} />
              <Route path="/assessment/results/:id" element={<Moved to={`${ASSESSMENT}/results/:id`} />} />
              <Route path="/assessment/history" element={<Moved to={`${ASSESSMENT}/history`} />} />
              <Route
                path="*"
                element={
                  <div className="max-w-3xl mx-auto px-6 py-24">
                    <div className="eyebrow mb-4">Not found</div>
                    <h1 className="editorial-h1 mb-4">
                      This page <em className="italic text-accent">isn't here.</em>
                    </h1>
                    <p className="editorial-deck mb-8">
                      We're still building. Come back soon, or head home.
                    </p>
                    <Link to="/account" className="btn-text">
                      Go to your account
                    </Link>
                  </div>
                }
              />
            </Routes>
          </Suspense>
          </PageErrorBoundary>
        </main>
        <Footer />
        <CookieConsent />
      </div>
    </AuthProvider>
  );
}
